"use client";

import * as React from "react";
import { ScrollEdgeArrows, useScrollEdges } from "./scroll-edges";

/**
 * The shared horizontal card row used by the home `video-strip` and
 * `more-content` blocks: a `flex gap-[32px]` row on desktop that becomes a
 * snap-scroll carousel (`overflow-x-auto snap-x`, 80%-basis cards) on mobile.
 * Only the row container is shared — each block supplies its own card children
 * (video card with play-overlay, or the more-content sublink column).
 *
 * - `desktopJustify="center"` adds `justify-center` (+ `max-md:justify-start`),
 *   matching video-strip; default "start".
 * - `fullWidth` adds `w-full`, matching more-content.
 *
 * The scrollbar is hidden here (`no-scrollbar`), which left nothing at all to
 * say the row continues past the edge of the screen — so it carries the shared
 * ‹ / › affordances, one per direction that can actually move. QA: "for any
 * horizontal scrollable content, add < and > arrow signs to indicate that the
 * content is scrollable, do not show < or > if the direction is not
 * scrollable."
 */
export function HorizontalCardCarousel({
  children,
  desktopJustify = "start",
  fullWidth = false,
  className,
}: {
  children: React.ReactNode;
  desktopJustify?: "start" | "center";
  fullWidth?: boolean;
  className?: string;
}) {
  // Each fragment is a WHOLE class string and they are joined with spaces —
  // never interpolated flush against another class. Tailwind's scanner reads
  // source text, and `…max-md:no-scrollbar${className…}` glues the last class
  // to `${`, which is not a valid candidate, so that class was silently never
  // generated: the mobile card rows kept their scrollbar even though the code
  // says otherwise. QA: "do not show the horizontal scrollbar".
  const desktopExtra = desktopJustify === "center" ? "justify-center" : "";
  const mobileExtra = desktopJustify === "center" ? "max-md:justify-start" : "";
  const wFull = fullWidth ? "w-full" : "";
  const ref = React.useRef<HTMLDivElement | null>(null);
  const edges = useScrollEdges(ref);

  return (
    <div className="relative">
      <div
        ref={ref}
        className={[
          "flex gap-[32px] items-start",
          "max-md:gap-[16px]",
          "max-md:overflow-x-auto max-md:snap-x max-md:snap-mandatory max-md:no-scrollbar",
          desktopExtra,
          wFull,
          mobileExtra,
          className ?? "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {children}
      </div>
      {/* Desktop lays the cards out in a row that always fits, so the arrows
          are a phone/tablet affordance only. */}
      <div className="md:hidden">
        <ScrollEdgeArrows scroller={ref} edges={edges} />
      </div>
    </div>
  );
}
